// src/components/billing/InvoiceTable.jsx - Fixed calculations
import React from 'react';
import { Link } from 'react-router-dom';
import { deleteDoc, doc } from 'firebase/firestore';
import { db } from '../../services/firebase';
import { printInvoice } from '../../utils/printUtils';
import { useAuth } from '../../contexts/AuthContext';
import { useNotification } from '../../contexts/NotificationContext';

const InvoiceTable = ({ invoices, loading, onRefresh }) => {
  const { vendorProfile } = useAuth();
  const { showAlert } = useNotification();

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount);
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('en-IN');
  };

  // Helper function to calculate CGST and SGST - FIXED
  const calculateCGSTSGST = (taxableAmount, gstRate) => {
    const totalGSTAmount = (taxableAmount * gstRate) / 100;
    const cgstAmount = totalGSTAmount / 2;
    const sgstAmount = totalGSTAmount / 2;
    return { cgstAmount, sgstAmount, totalGSTAmount };
  };

  // Calculate actual total amount with GST for display - FIXED
  const calculateActualTotal = (invoice) => {
    if (!invoice) return 0;

    const discountPercentage = parseFloat(invoice.discount) || 0;
    const subtotal = invoice.subtotal || 0;
    const discountAmount = (subtotal * discountPercentage) / 100;

    if (invoice.invoiceType === 'with_gst' && invoice.items && Array.isArray(invoice.items)) {
      let totalGSTAmount = 0;
      invoice.items.forEach(item => {
        const itemDiscountAmount = (item.amount || 0) * discountPercentage / 100;
        const itemTaxableAmount = (item.amount || 0) - itemDiscountAmount;
        const { totalGSTAmount: itemGST } = calculateCGSTSGST(itemTaxableAmount, item.gstRate || 0);
        totalGSTAmount += itemGST;
      });
      return subtotal - discountAmount + totalGSTAmount;
    }

    // Fallback to stored total or calculate without GST
    return invoice.totalAmount || (subtotal - discountAmount);
  };

  // Calculate actual GST amount for display - FIXED
  const calculateActualGST = (invoice) => {
    if (!invoice || invoice.invoiceType !== 'with_gst') return 0;

    if (invoice.items && Array.isArray(invoice.items)) {
      const discountPercentage = parseFloat(invoice.discount) || 0;
      let totalGSTAmount = 0;
      
      invoice.items.forEach(item => {
        const itemDiscountAmount = (item.amount || 0) * discountPercentage / 100;
        const itemTaxableAmount = (item.amount || 0) - itemDiscountAmount;
        const { totalGSTAmount: itemGST } = calculateCGSTSGST(itemTaxableAmount, item.gstRate || 0);
        totalGSTAmount += itemGST;
      });
      
      return totalGSTAmount;
    }

    // Fallback to stored tax amount
    return invoice.totalTaxAmount || 0;
  };

  const handlePrintInvoice = (invoice) => {
    const success = printInvoice(invoice, vendorProfile);
    if (!success) {
      showAlert('Please allow popups to print invoices', 'error');
    }
  };

  const handleDeleteInvoice = async (invoiceId) => {
    if (!window.confirm('Are you sure you want to delete this invoice? This action cannot be undone.')) {
      return;
    }

    try {
      await deleteDoc(doc(db, 'invoices', invoiceId));
      showAlert('Invoice deleted successfully', 'success');
      onRefresh();
    } catch (error) {
      console.error('Error deleting invoice:', error);
      showAlert('Failed to delete invoice', 'error');
    }
  };

  const handleSendWhatsApp = (invoice) => {
    try {
      const companyName = vendorProfile?.shopName || 'Your Company';
      const invoiceUrl = `${window.location.origin}/billing/invoice/${invoice.id}`;
      const itemsCount = invoice.items?.length || 0;
      const totalQuantity = invoice.items?.reduce((sum, item) => sum + (item.quantity || 0), 0) || 0;
      const actualTotal = calculateActualTotal(invoice);
     
      const message = encodeURIComponent(
        `Dear ${invoice.customerName || 'Customer'},

Thank you for your business!

*Invoice Details:*
📋 Invoice Number: ${invoice.invoiceNumber}
🏪 From: ${companyName}
💰 Amount: ${formatCurrency(actualTotal)}
📅 Date: ${formatDate(invoice.createdAt)}
📦 Items: ${itemsCount} (Total Qty: ${totalQuantity})
✅ Status: Paid
${invoice.invoiceType === 'with_gst' ? '🧾 GST Invoice' : '📄 Non-GST Invoice'}

View Invoice: ${invoiceUrl}

Please contact us for any queries.
Best regards,
${companyName}`
      );

      const whatsappUrl = `https://wa.me/?text=${message}`;
      window.open(whatsappUrl, '_blank');
    } catch (error) {
      console.error('Error generating WhatsApp link:', error);
      showAlert('Failed to generate WhatsApp link', 'error');
    }
  };

  const getStatusBadge = (status) => {
    return (
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
        <svg className="h-3 w-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
        </svg>
        Paid
      </span>
    );
  };

  const getInvoiceTypeBadge = (invoiceType) => {
    if (invoiceType === 'with_gst') {
      return (
        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
          GST
        </span>
      );
    }
    return (
      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
        Non-GST
      </span>
    );
  };

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Invoice Details
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Customer
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Items & Amount
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Status & Type
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Date
            </th>
            <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
              Actions
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {loading ? (
            <tr>
              <td colSpan="6" className="px-6 py-4 text-center">
                <div className="flex justify-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-600"></div>
                </div>
                <p className="mt-2 text-sm text-gray-500">Loading invoices...</p>
              </td>
            </tr>
          ) : invoices.length === 0 ? (
            <tr>
              <td colSpan="6" className="px-6 py-4 text-center text-sm text-gray-500">
                <div className="flex flex-col items-center py-8">
                  <svg className="h-12 w-12 text-gray-400 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <p className="text-lg font-medium text-gray-900 mb-1">No sales invoices found</p>
                  <p className="text-sm text-gray-500">Create your first invoice to get started.</p>
                </div>
              </td>
            </tr>
          ) : (
            invoices.map((invoice) => {
              const actualTotal = calculateActualTotal(invoice);
              const actualGST = calculateActualGST(invoice);
              
              return (
                <tr key={invoice.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div>
                      <Link
                        to={`/billing/invoice/${invoice.id}`}
                        className="text-orange-600 hover:text-orange-900 font-medium"
                      >
                        {invoice.invoiceNumber}
                      </Link>
                      <div className="text-sm text-gray-500">
                        Created: {formatDate(invoice.createdAt)}
                      </div>
                      {invoice.notes && (
                        <div className="text-xs text-gray-400 mt-1 truncate max-w-xs">
                          Note: {invoice.notes}
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div>
                      <div className="text-sm font-medium text-gray-900">
                        {invoice.customerName}
                      </div>
                      {invoice.customerEmail && (
                        <div className="text-sm text-gray-500">
                          {invoice.customerEmail}
                        </div>
                      )}
                      {invoice.customerPhone && (
                        <div className="text-sm text-gray-500">
                          {invoice.customerPhone}
                        </div>
                      )}
                      {invoice.customerGST && (
                        <div className="text-xs text-blue-600 font-medium">
                          GST: {invoice.customerGST}
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div>
                      <div className="text-sm font-medium text-gray-900">
                        {formatCurrency(actualTotal)}
                      </div>
                      <div className="text-sm text-gray-500">
                        {invoice.items?.length || 0} items
                      </div>
                      <div className="text-sm text-gray-500">
                        Qty: {invoice.items?.reduce((sum, item) => sum + (item.quantity || 0), 0) || 0}
                      </div>
                      {(invoice.discount != null && parseFloat(invoice.discount) > 0) && (
                        <div className="text-xs text-green-600">
                          Discount: {parseFloat(invoice.discount)}%
                        </div>
                      )}
                      {invoice.invoiceType === 'with_gst' && actualGST > 0 && (
                        <div className="text-xs text-gray-500">
                          GST: {formatCurrency(actualGST)}
                        </div>
                      )}
                    </div>
                  </td>
             
<td className="px-6 py-4 whitespace-nowrap">
  <div className="space-y-1">
    {getStatusBadge(invoice.status)}
    {getInvoiceTypeBadge(invoice.invoiceType)}
    {/* ADD THIS NEW LINE HERE */}
    {invoice.saleMode === 'online' && (
      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
        🚚 Online
      </span>
    )}
  </div>
</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    <div>
                      <div className="text-s text-green-600">{formatDate(invoice.createdAt)}</div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-center">
                    <div className="flex flex-col items-center space-y-3">
                      {/* Primary Action Buttons Row */}
                      <div className="flex items-center space-x-3">
                        <Link
                          to={`/billing/invoice/${invoice.id}`}
                          className="inline-flex items-center justify-center w-10 h-10 rounded-lg bg-blue-50 text-blue-600 hover:bg-blue-100 hover:text-blue-700 transition-all duration-200 touch-manipulation"
                          title="View Invoice"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                          </svg>
                        </Link>
                        <button
                          onClick={() => handlePrintInvoice(invoice)}
                          className="inline-flex items-center justify-center w-10 h-10 rounded-lg bg-gray-50 text-gray-600 hover:bg-gray-100 hover:text-gray-700 transition-all duration-200 touch-manipulation"
                          title="Print Invoice"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                          </svg>
                        </button>
                      </div>

                      {/* Secondary Action Buttons Row */}
                      <div className="flex items-center space-x-3">
                        <button
                          onClick={() => handleSendWhatsApp(invoice)}
                          className="inline-flex items-center justify-center w-10 h-10 rounded-lg bg-green-50 text-green-600 hover:bg-green-100 hover:text-green-700 transition-all duration-200 touch-manipulation"
                          title="Send via WhatsApp"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                          </svg>
                        </button>
                        <button
                          onClick={() => handleDeleteInvoice(invoice.id)}
                          className="inline-flex items-center justify-center w-10 h-10 rounded-lg bg-red-50 text-red-600 hover:bg-red-100 hover:text-red-700 transition-all duration-200 touch-manipulation"
                          title="Delete Invoice"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  </td>
                </tr>
              );
            })
          )}
        </tbody>
      </table>
    </div>
  );
};

export default InvoiceTable;